import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useRouter, useRouterState } from '@tanstack/react-router';
import { FileText, MoreHorizontal, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { RICH_TEXT_CLASS, RICH_TEXT_PREVIEW_CLASS } from '@/lib/rich-text';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { DetailPageLayout } from '@/components/layouts';
import {
  CollapsibleSectionHeader,
  DataTableDrawerHeader,
  FormAccordionCard,
} from '@/components/molecules';
import { TermsTemplateFormContent } from '@/components/organisms/settings-terms-templates/TermsTemplateFormDrawer';
import { useTermsTemplateForm } from '@/components/organisms/settings-terms-templates/useTermsTemplateForm';
import {
  useTermsTemplates,
  useCreateTermsTemplate,
  useUpdateTermsTemplate,
  useDeleteTermsTemplate,
  useSetDefaultTermsTemplate,
  termsTypeLabels,
  type TermsTemplate,
  type TermsType,
} from '@/hooks/settings/useTermsTemplates';
import { useHasPermission } from '@/hooks/usePermissions';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';

export const Route = createFileRoute('/_dashboard/dashboard/settings/terms-and-conditions')({
  // Which tab opens: a purchase order's "manage terms" link opens Purchase.
  validateSearch: (search: Record<string, unknown>): { type?: TermsType } =>
    search.type === 'PURCHASE' || search.type === 'SALES' ? { type: search.type } : {},
  component: TermsAndConditionsPage,
});

/** What each type of terms is for, under the tabs. */
const TYPE_DESCRIPTIONS: Record<TermsType, string> = {
  SALES:
    'Terms for customers. Job types and estimate templates point at them; jobs, estimates, invoices and proposals inherit them and can override.',
  PURCHASE:
    'Terms for suppliers, printed on purchase orders. The default goes on every new order; an order can stack more of them or use its own.',
};

const DETAIL_PATTERN = /^\/dashboard\/settings\/terms-and-conditions\/([^/]+)\/?$/;
const EDIT_PATTERN = /^\/dashboard\/settings\/terms-and-conditions\/([^/]+)\/edit\/?$/;
const EMPTY_TEMPLATE_VALUES = { name: '', content: '' };

function TermsAndConditionsPage() {
  const router = useRouter();
  const { location } = useRouterState();
  const canSales = useHasPermission('estimates:view');
  const canPurchase = useHasPermission('purchase_orders:view');
  const purchasingEnabled = usePurchasingEnabled();
  // Purchase terms go with purchasing: only tenants with it turned on see them.
  const tabs = (['SALES', 'PURCHASE'] as const).filter((type) =>
    type === 'SALES' ? canSales : canPurchase && purchasingEnabled,
  );
  const opened = Route.useSearch().type;
  const [tab, setTab] = useState<TermsType>(opened ?? 'SALES');
  const shownTab = tabs.includes(tab) ? tab : (tabs[0] ?? 'SALES');
  const { data: allTemplates = [], isLoading } = useTermsTemplates('ALL');
  const templates = allTemplates.filter((template) => template.type === shownTab);
  const createMutation = useCreateTermsTemplate();
  const updateMutation = useUpdateTermsTemplate();
  const deleteMutation = useDeleteTermsTemplate();
  const setDefaultMutation = useSetDefaultTermsTemplate();

  const [deleteTarget, setDeleteTarget] = useState<TermsTemplate | null>(null);

  const editId = location.pathname.match(EDIT_PATTERN)?.[1];
  const detailId = !editId ? location.pathname.match(DETAIL_PATTERN)?.[1] : undefined;
  const decodedEditId = editId ? decodeURIComponent(editId) : undefined;
  const decodedDetailId = detailId ? decodeURIComponent(detailId) : undefined;

  const detailTemplate = useMemo(
    () => allTemplates.find((t) => t.id === decodedDetailId) ?? null,
    [decodedDetailId, allTemplates],
  );
  const editTemplate = useMemo(
    () => allTemplates.find((t) => t.id === decodedEditId) ?? null,
    [decodedEditId, allTemplates],
  );

  const { values, setValues, isValid } = useTermsTemplateForm();
  const hydratedEditIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!decodedEditId) {
      hydratedEditIdRef.current = null;
      return;
    }

    if (decodedEditId === 'new') {
      if (hydratedEditIdRef.current !== decodedEditId) {
        setValues({ ...EMPTY_TEMPLATE_VALUES, type: shownTab });
      }
      hydratedEditIdRef.current = decodedEditId;
      return;
    }

    if (editTemplate && hydratedEditIdRef.current !== decodedEditId) {
      setValues({
        name: editTemplate.name,
        content: editTemplate.content,
        type: editTemplate.type,
      });
      hydratedEditIdRef.current = decodedEditId;
    }
  }, [decodedEditId, editTemplate, setValues, shownTab]);

  function openDetail(id: string) {
    void router.navigate({
      to: '/dashboard/settings/terms-and-conditions/$id',
      params: { id },
    });
  }

  function openEdit(template: TermsTemplate) {
    setValues({ name: template.name, content: template.content, type: template.type });
    void router.navigate({
      to: '/dashboard/settings/terms-and-conditions/$id/edit',
      params: { id: template.id },
    });
  }

  function openNew() {
    setValues({ ...EMPTY_TEMPLATE_VALUES, type: shownTab });
    void router.navigate({
      to: '/dashboard/settings/terms-and-conditions/$id/edit',
      params: { id: 'new' },
    });
  }

  function closeDrawer() {
    void router.navigate({ to: '/dashboard/settings/terms-and-conditions' });
  }

  function handleSave() {
    if (!editTemplate || !isValid) return;
    updateMutation.mutate(
      { id: editTemplate.id, name: values.name, content: values.content },
      {
        onSuccess: () => {
          toast.success('Template updated.');
          openDetail(editTemplate.id);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to update template'),
      },
    );
  }

  function handleCreate() {
    if (!isValid) return;
    createMutation.mutate(values, {
      onSuccess: (created) => {
        toast.success('Template created.');
        setTab(created.type);
        closeDrawer();
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to create template'),
    });
  }

  function handleSetDefault(template: TermsTemplate) {
    setDefaultMutation.mutate(template.id, {
      onSuccess: () => toast.success(`"${template.name}" is now the default.`),
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to set default'),
    });
  }

  function handleDelete() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Template deleted.');
        setDeleteTarget(null);
        closeDrawer();
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to delete template'),
    });
  }

  const sheetOpen = Boolean(decodedDetailId || decodedEditId);

  return (
    <DetailPageLayout
      header={
        <>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Terms &amp; Conditions</h1>
            <p className="text-xs text-muted-foreground">{TYPE_DESCRIPTIONS[shownTab]}</p>
          </div>
          <Button size="sm" onClick={openNew}>
            <Plus className="h-4 w-4" />
            Add template
          </Button>
        </>
      }
    >
      {tabs.length > 1 ? (
        <div role="tablist" aria-label="Type of terms" className="mb-4 flex gap-1.5">
          {tabs.map((type) => (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={shownTab === type}
              onClick={() => setTab(type)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                shownTab === type
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground',
              )}
            >
              {termsTypeLabels[type]}
              <span className="ml-1.5 tabular-nums opacity-70">
                {allTemplates.filter((template) => template.type === type).length}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No {termsTypeLabels[shownTab].toLowerCase()} terms yet. Add one to get started.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <div
              key={template.id}
              className="group flex cursor-pointer flex-col rounded-lg border bg-card transition-colors hover:bg-muted/40"
              onClick={() => openDetail(template.id)}
            >
              {/* Card header */}
              <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="truncate text-sm font-medium">{template.name}</p>
                  {template.isDefault ? (
                    <Badge variant="secondary" className="shrink-0 gap-1">
                      <Star className="h-3 w-3 fill-current" />
                      Default
                    </Badge>
                  ) : null}
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(template)}>
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Edit
                      </DropdownMenuItem>
                      {template.isDefault ? null : (
                        <DropdownMenuItem
                          onClick={() => handleSetDefault(template)}
                          disabled={setDefaultMutation.isPending}
                        >
                          <Star className="mr-2 h-3.5 w-3.5" />
                          Set as default
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => setDeleteTarget(template)}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {/* Card content preview — fixed height + fade-out instead of
                  line-clamp: the content is multi-block rich HTML, where
                  -webkit-line-clamp slices a line in half. The fade reads as an
                  intentional cut, keeps the rich formatting, and uniforms card
                  height (short templates sit in the opaque zone, fade falls on
                  empty space). Mask goes to transparent → works in light + dark. */}
              <div
                className={cn(
                  RICH_TEXT_PREVIEW_CLASS,
                  'h-24 overflow-hidden px-4 py-3 text-xs text-muted-foreground',
                )}
                style={{
                  // Fade only the bottom ~16px so content that actually fits stays
                  // fully opaque (a 3-line preview shouldn't look "cut off"); only
                  // genuinely overflowing content gets softened.
                  WebkitMaskImage:
                    'linear-gradient(to bottom, #000 calc(100% - 16px), transparent)',
                  maskImage: 'linear-gradient(to bottom, #000 calc(100% - 16px), transparent)',
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.content) }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Sheet — detail or edit */}
      <Sheet open={sheetOpen} onOpenChange={(open) => !open && closeDrawer()}>
        <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-xl" showCloseButton={false}>
          {decodedEditId ? (
            <EditView
              isNew={decodedEditId === 'new'}
              template={decodedEditId === 'new' ? null : editTemplate}
              values={values}
              onChange={setValues}
              isValid={isValid}
              isSaving={createMutation.isPending || updateMutation.isPending}
              onSave={decodedEditId === 'new' ? handleCreate : handleSave}
              onClose={closeDrawer}
              onBack={
                decodedEditId !== 'new' && editTemplate
                  ? () => openDetail(editTemplate.id)
                  : undefined
              }
            />
          ) : detailTemplate ? (
            <DetailView
              template={detailTemplate}
              onEdit={() => openEdit(detailTemplate)}
              onClose={closeDrawer}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteTarget?.name}&rdquo; will be permanently deleted.{' '}
              {deleteTarget?.type === 'PURCHASE'
                ? 'Purchase orders already using it keep the terms they were saved with.'
                : 'Estimates and jobs already using it keep the terms they were saved with.'}
              {deleteTarget?.isDefault
                ? ' It is the current default, so the oldest remaining template of its type takes over.'
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DetailPageLayout>
  );
}

function DetailView({
  template,
  onEdit,
  onClose,
}: {
  template: TermsTemplate;
  onEdit: () => void;
  onClose: () => void;
}) {
  const purchasingEnabled = usePurchasingEnabled();
  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <SheetTitle className="flex items-center gap-2 text-sm font-semibold">
            {template.name}
            {purchasingEnabled ? (
              <Badge variant="outline">{termsTypeLabels[template.type]}</Badge>
            ) : null}
            {template.isDefault ? (
              <Badge variant="secondary" className="gap-1">
                <Star className="h-3 w-3 fill-current" />
                Default
              </Badge>
            ) : null}
          </SheetTitle>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" aria-label="Edit" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetHeader>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <FormAccordionCard
          open
          header={
            <CollapsibleSectionHeader
              icon={FileText}
              label="Content"
              open
              onToggle={() => undefined}
            />
          }
        >
          <div
            className={cn(RICH_TEXT_CLASS, 'text-sm text-foreground')}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.content) }}
          />
        </FormAccordionCard>
      </div>
    </>
  );
}

function EditView({
  isNew,
  template,
  values,
  onChange,
  isValid,
  isSaving,
  onSave,
  onClose,
  onBack,
}: {
  isNew: boolean;
  template: TermsTemplate | null;
  values: { name: string; content: string; type?: TermsType };
  onChange: (v: { name: string; content: string; type?: TermsType }) => void;
  isValid: boolean;
  isSaving: boolean;
  onSave: () => void;
  onClose: () => void;
  onBack?: () => void;
}) {
  const purchasingEnabled = usePurchasingEnabled();
  return (
    <>
      <DataTableDrawerHeader
        title={isNew ? 'New Template' : 'Edit Template'}
        onClose={onClose}
        onBack={onBack}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        {!isNew && !template ? (
          <p className="text-sm text-muted-foreground">Template not found.</p>
        ) : (
          <TermsTemplateFormContent
            values={values}
            onChange={onChange}
            typeEditable={isNew && purchasingEnabled}
          />
        )}
      </div>

      <SheetFooter className="flex-row justify-end border-t px-5 py-4">
        <Button type="button" variant="outline" onClick={onBack ?? onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={!isValid || isSaving}>
          {isSaving ? (isNew ? 'Creating...' : 'Saving...') : isNew ? 'Create' : 'Save'}
        </Button>
      </SheetFooter>
    </>
  );
}
